// Shared FR12 / FR14 logic: what QC coverage is required for a run,
// and is the run currently on-track? Used by:
//   - QC page header banner (live coverage counter)
//   - Runs page Verify & Publish gate (blocks verification if insufficient)
//
// Bands follow getSampleGuidance — kept in this file so the logic has one home.

export interface SampleGuidance {
  label:       string
  detail:      string
  urgency:     'normal' | 'elevated' | 'high'
  ratePerHour: number
}

export function getSampleGuidance(packoutPct: number | null): SampleGuidance {
  if (packoutPct == null || packoutPct >= 88)
    return { label: 'Standard',  detail: '1 box every 2 hrs',  urgency: 'normal',   ratePerHour: 0.5 }
  if (packoutPct >= 85)
    return { label: 'Increased', detail: '1 box per hour',     urgency: 'elevated', ratePerHour: 1   }
  return   { label: 'High Freq', detail: '1 box every 30 min', urgency: 'high',     ratePerHour: 2   }
}

export interface QcCoverage {
  expected:  number
  actual:    number
  shortfall: number
  onTrack:   boolean
}

export function computeQcCoverage(
  startedAt: string,
  packoutPct: number | null,
  actualCount: number,
  endAt?: string | null,   // cap the window at completed_at for a stopped run (else now)
): QcCoverage {
  const endTs = endAt ? new Date(endAt).getTime() : Date.now()
  const hours = Math.max(0, (endTs - new Date(startedAt).getTime()) / 3_600_000)
  const rate  = getSampleGuidance(packoutPct).ratePerHour
  const expected = Math.floor(hours * rate)
  const shortfall = Math.max(0, expected - actualCount)
  return { expected, actual: actualCount, shortfall, onTrack: shortfall === 0 }
}

// FR14: Can this run be verified & published?
// Reasons it's blocked feed the Manager's UI so they understand why.
export interface VerificationCheck {
  canVerify: boolean
  blockers:  string[]   // human-readable reasons
  warnings:  string[]   // non-blocking flags to surface alongside
}

export interface VerificationInput {
  status:                  string
  started_at:              string
  completed_at:            string | null
  packout_percent:         number | null
  full_box_count:          number
  packer_problem_count:    number
  packer_without_photo:    number
  titration_warning_count: number
  titration_warning_no_photo: number
}

const LOW_PACKOUT_THRESHOLD = 85

export function checkVerification(r: VerificationInput): VerificationCheck {
  const blockers: string[] = []
  const warnings: string[] = []

  if (r.status !== 'COMPLETE') {
    blockers.push('Run must be marked Complete before verification')
  }

  // Coverage check uses completed_at if available (end of window) or now.
  // The shortfall is surfaced as a warning so the manager sees the gap,
  // but no longer blocks Verify & Publish — operators kept hitting it
  // on light shifts where the sample target is unrealistic. Will be
  // re-tightened (back to a blocker) once the rate bands are tuned.
  const endTs = r.completed_at ? new Date(r.completed_at).getTime() : Date.now()
  const hours = Math.max(0, (endTs - new Date(r.started_at).getTime()) / 3_600_000)
  const rate  = getSampleGuidance(r.packout_percent).ratePerHour
  const expected = Math.floor(hours * rate)
  const shortfall = Math.max(0, expected - r.full_box_count)
  if (shortfall > 0) {
    warnings.push(`QC coverage light: ${shortfall} more full-box sample${shortfall === 1 ? '' : 's'} would meet the recommended rate for this packout band`)
  }

  // FR14: low packout requires defect photos on evidence-class QC logs.
  const isLowPackout = r.packout_percent != null && r.packout_percent < LOW_PACKOUT_THRESHOLD
  if (isLowPackout) {
    if (r.packer_problem_count === 0 && r.titration_warning_count === 0) {
      warnings.push(`Packout is ${r.packout_percent!.toFixed(1)}% but no defect QC logged — investigate before publishing`)
    }
    if (r.packer_without_photo > 0) {
      blockers.push(`${r.packer_without_photo} Packer QC log${r.packer_without_photo === 1 ? '' : 's'} missing photo evidence (required for low-packout runs)`)
    }
    if (r.titration_warning_no_photo > 0) {
      blockers.push(`${r.titration_warning_no_photo} Titration warning${r.titration_warning_no_photo === 1 ? '' : 's'} missing photo evidence`)
    }
  }

  return { canVerify: blockers.length === 0, blockers, warnings }
}
